import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, FileText, Loader2, Mail, X } from 'lucide-react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { Button } from '@/components/ui/button';
import { fetchCoreQuestionnaireSections } from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';

export interface InviteQuestionnaireOption {
  id: string;
  label: string;
}

interface InviteQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  clientFirstName: string;
  clientLastName?: string;
  clientEmail: string;
  selectedQuestionnaireIds: string[];
  onAttach: (questionnaireIds: string[], selectedOptions?: InviteQuestionnaireOption[]) => void;
  eyebrowText?: string;
  titleText?: string;
  helperText?: string;
  attachButtonText?: string;
}

const InviteQuestionnaireModal: React.FC<InviteQuestionnaireModalProps> = ({
  open,
  onClose,
  clientFirstName,
  clientLastName,
  clientEmail,
  selectedQuestionnaireIds,
  onAttach,
  eyebrowText = 'Questionnaire',
  titleText = 'Onboarding Questionnaires',
  helperText = 'You can add your own custom questionnaire after creating the client profile.',
  attachButtonText = 'Attach Questionnaire',
}) => {
  const nutritionistId = useMemo(() => getCurrentUser()?.id, []);
  const [draftSelection, setDraftSelection] = useState<string[]>(selectedQuestionnaireIds);

  const coreQuestionnairesQuery = useQuery({
    queryKey: ['questionnaireSections', 'client-onboarding', 'core', nutritionistId],
    queryFn: () => fetchCoreQuestionnaireSections(nutritionistId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const questionnaireOptions = useMemo<InviteQuestionnaireOption[]>(() => {
    const questionnaires = coreQuestionnairesQuery.data?.questionnaires ?? [];
    return questionnaires
      .filter((questionnaire) => questionnaire.isActive !== false && Boolean(questionnaire.id))
      .map((questionnaire) => ({
        id: questionnaire.id,
        label: questionnaire.title || questionnaire.name || 'Untitled questionnaire',
      }));
  }, [coreQuestionnairesQuery.data?.questionnaires]);

  const activeQuestionnaireIds = useMemo(
    () => new Set(questionnaireOptions.map((option) => option.id)),
    [questionnaireOptions]
  );

  useEffect(() => {
    if (!open) return;
    setDraftSelection(selectedQuestionnaireIds);
  }, [open, selectedQuestionnaireIds]);

  useEffect(() => {
    if (!open || questionnaireOptions.length === 0) return;
    setDraftSelection((prev) => prev.filter((id) => activeQuestionnaireIds.has(id)));
  }, [activeQuestionnaireIds, open, questionnaireOptions.length]);

  const safeEmail = clientEmail?.trim() || 'client@email.com';
  const displayName = [clientFirstName?.trim(), clientLastName?.trim()].filter(Boolean).join(' ') || 'Client';
  const initials = useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean);
    if (parts.length === 0) return 'C';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }, [displayName]);

  const toggleQuestionnaire = (id: string) => {
    setDraftSelection((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const validDraftSelection = useMemo(
    () => draftSelection.filter((id) => activeQuestionnaireIds.has(id)),
    [activeQuestionnaireIds, draftSelection]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.48)',
        },
        '& .MuiDialog-paper': {
          m: 2,
          width: 'min(100%, 900px)',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0px 24px 64px rgba(15, 23, 42, 0.2)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
            width: 28,
            height: 28,
            borderRadius: '10px',
            color: '#6b7280',
          }}
        >
          <X size={15} />
        </IconButton>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRight: { xs: 'none', md: '1px solid #E5E7EB' },
              borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
              minHeight: { md: '470px' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>
              {eyebrowText}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '34px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
              {titleText}
            </Typography>

            <Box
              sx={{
                mt: 3,
                p: 1.5,
                border: '1px solid #4DA3FF',
                borderRadius: '10px',
                backgroundColor: '#F5FAFF',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <AlertCircle size={16} color="#20AEDC" />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#334155', lineHeight: '20px' }}>
                {helperText}
              </Typography>
            </Box>

            <Box sx={{ mt: 'auto', pt: 4 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                Receiver details
              </Typography>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    border: '1px solid #4DA3FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '18px',
                    color: '#20AEDC',
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </Box>

                <Box>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 500, color: '#1F2937', lineHeight: 1.2 }}>
                    {displayName}
                  </Typography>
                  <Typography sx={{ mt: 0.3, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#4B5563' }}>
                    <Mail size={14} />
                    {safeEmail}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 3.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 700, color: '#111827', mb: 1 }}>
              Select Questionnaire
            </Typography>

            {coreQuestionnairesQuery.isLoading ? (
              <Box
                sx={{
                  minHeight: '180px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  color: '#6B7280',
                }}
              >
                <Loader2 size={16} className="animate-spin" />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  Loading questionnaires...
                </Typography>
              </Box>
            ) : coreQuestionnairesQuery.isError ? (
              <Box
                sx={{
                  minHeight: '180px',
                  border: '1px solid #FCA5A5',
                  borderRadius: '12px',
                  backgroundColor: '#FEF2F2',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#991B1B',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '20px' }}>
                  Unable to load core questionnaires. Please close and try again.
                </Typography>
              </Box>
            ) : questionnaireOptions.length === 0 ? (
              <Box
                sx={{
                  minHeight: '180px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6B7280',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '20px' }}>
                  No active core questionnaires are available.
                </Typography>
              </Box>
            ) : questionnaireOptions.map((option) => {
              const checked = draftSelection.includes(option.id);
              return (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  onClick={() => toggleQuestionnaire(option.id)}
                  sx={{
                    width: '100%',
                    minHeight: '56px',
                    borderRadius: '12px',
                    border: `1px solid ${checked ? '#20AEDC' : '#E5E7EB'}`,
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.2,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      borderColor: '#20AEDC',
                      backgroundColor: '#F8FCFF',
                    },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleQuestionnaire(option.id)}
                    size="small"
                    sx={{
                      p: 0.6,
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#20AEDC',
                      },
                    }}
                  />
                  <FileText size={16} color="#6B7280" />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', color: '#1F2937', lineHeight: 1.2 }}>
                    {option.label}
                  </Typography>
                </Box>
              );
            })}

            <Button
              variant="primary"
              size="small"
              fullWidth
              className="mt-2"
              disabled={validDraftSelection.length === 0 || coreQuestionnairesQuery.isLoading}
              onClick={() => {
                const selectedItems = questionnaireOptions.filter(o => validDraftSelection.includes(o.id));
                onAttach(validDraftSelection, selectedItems);
              }}
            >
              {attachButtonText}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InviteQuestionnaireModal;
